import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type AgentCap, type Device, type Recording, type ShareSummary } from "../api";
import { ShareRow } from "../components/ShareRow";
import { useConfirm } from "../components/Confirm";
import { useEditTitle } from "../components/EditTitle";
import { t, word, phrases } from "../i18n";
import { RecordingList } from "../components/RecordingList";
import { Drawer } from "../components/Drawer";
import { DeviceList } from "../components/DeviceList";
import { FilterChips, inForce, useFilter, type Chip } from "../components/FilterChips";
import { SelectControls, useSelectMode } from "../components/Selection";
import { deletable, shareActions, shareMenu, type ShareActions } from "../components/ShareActions";
import { Page, PageHead } from "../components/Page";
import { Grouped, liveThenEarlier } from "../components/Rows";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { useKeptChoice } from "../kept";
import { useShareList } from "../shareList";
import { agentKinds, kindOf, liveThenNewest, sectionOf } from "../kinds";

// /shares, by kind (byj-1wv.45), as tabs: Terminals, Agents — byj, Claude
// Code, Codex, A2A, narrowed by a chip — and Recordings, each a list of rows,
// live ones first under their own heading; then Class recordings, those made
// in a class, under each class (byj-1wv.51.6), a tab only for someone who has
// any. The tab is kept per browser.
// Devices open in a drawer from the page's head. A row's menu, and its
// rename and delete-or-remove, are the group page's too (components/ShareActions),
// as is how the list is read and kept current (shareList.ts).

/** How often the list is read again while a share in it is live (byj-fym). */
export const sharesPollMs = 30_000;

type Tab = "terminals" | "agents" | "recordings" | "class-recordings";
const tabs: Tab[] = ["terminals", "agents", "recordings", "class-recordings"];
const TabKey = "shares.tab";

const useTab = () => useKeptChoice<Tab>(TabKey, "agents", tabs);

/** One kind's shares: its toolbar (chips, for agents), then its rows, Select over the ended ones. */
function ShareSection({ id, title, shares, chips, empty, actions }: {
  id: string;
  title: string;
  shares: ShareSummary[] | null;
  chips?: boolean;
  empty: string;
  actions: ShareActions;
}) {
  const mode = useSelectMode();
  const [picks, setKind] = useFilter(id);
  const all = shares ?? [];
  const chipList: Chip[] = [
    { value: "all", label: t("classes:filterAll"), count: all.length },
    ...agentKinds.map((k) => ({ value: k, label: word("shares:kindChip", k) ?? k, count: all.filter((s) => kindOf(s.source) === k).length })),
  ];
  const kind = chips ? inForce(picks, chipList) : "all";
  const shown = (kind !== "all" ? all.filter((s) => kindOf(s.source) === kind) : all).slice().sort(liveThenNewest);
  // picking several is clearing out what has ended: the Earlier rows that
  // may go, in view; a live share shared with me leaves from its own menu
  const pickable = (s: ShareSummary) => !s.live && deletable(s);
  const selectable = shown.filter(pickable).map((s) => s.id);
  const dropPicked = async () => {
    const done = await actions.dropMany(selectable.filter((x) => mode.picked.has(x)));
    mode.keepOnly(selectable.filter((x) => !done.includes(x)));
  };

  if (shares === null) return <p className="muted">{t("common:loading")}</p>;
  if (all.length === 0) return <p className="muted">{empty}</p>;
  return (
    <Grouped
      id={id}
      tools={chips && <FilterChips label={t("shares:filterBy", { what: title })} value={kind} onChange={setKind} chips={chipList} />}
      // Select stands over the rows it reaches: Earlier's
      groups={liveThenEarlier(shown, <SelectControls ids={selectable} mode={mode} onDelete={() => void dropPicked()} />)}
      row={(s) => (
        <ShareRow
          key={s.id}
          share={s}
          menu={shareMenu(s, actions)}
          select={mode.selecting && pickable(s) ? { picked: mode.picked, toggle: mode.toggle } : undefined}
        />
      )}
    />
  );
}

// the list is the answer itself
const theShares = (ss: ShareSummary[]) => ss;
const asIs = (_: ShareSummary[], ss: ShareSummary[]) => ss;

export function Shares() {
  // read again while one is live or not connected (shareList.ts, byj-fym)
  const list = useShareList(api.shares, sharesPollMs, theShares, asIs);
  const shares = list.data;
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [devicesOpen, setDevicesOpen] = useState(false);
  // the Recordings tab's counts, from the list once it has loaded
  const [recordings, setRecordings] = useState<{ n: number; toReview: number } | null>(null);
  const countRecordings = useCallback((n: number, toReview: number) => setRecordings({ n, toReview }), []);
  const [classRecordings, setClassRecordings] = useState<{ n: number; toReview: number } | null>(null);
  const countClassRecordings = useCallback((n: number, toReview: number) => setClassRecordings({ n, toReview }), []);
  // one request for both lists of recordings, each keeping its own: the two
  // asking together share the one out, and a list asking again once it is
  // answered — after a review request (byj-xgt) — reads them afresh; kept
  // for good, it handed back the rows as they first came (byj-1wv.79)
  const loadRecordings = useMemo(() => {
    let out: Promise<Recording[]> | null = null;
    return () =>
      (out ??= api.recordings().finally(() => {
        out = null;
      }));
  }, []);
  const [tab, setTab] = useTab();
  // the owner's month across their agent shares (byj-1wv.46); null while
  // unknown, and left out by a relay that cannot say
  const [agentCap, setAgentCap] = useState<AgentCap | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [editTitle, editDialog] = useEditTitle();
  const { error, fail, gone, renamed } = list;

  // the devices beside the list, read with it and again on a retry
  const loadDevices = useCallback(() => api.devices().then(setDevices, fail), [fail]);
  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);
  const load = () => {
    void list.load();
    void loadDevices();
  };

  useEffect(() => {
    api.agentCap().then(setAgentCap, () => setAgentCap(null));
  }, []);

  const changeCap = async (cap: AgentCap) => {
    const answer = await editTitle(t("shares:agentCapHeading"), String(cap.cap_usd), t("shares:agentCapLabel"));
    if (answer === null) return;
    const usd = Number(answer.replace(/^\$/, ""));
    if (!Number.isFinite(usd) || usd <= 0) {
      fail(t("shares:agentCapBad"));
      return;
    }
    try {
      setAgentCap(await api.setAgentCap(usd));
    } catch (err) {
      fail(err);
    }
  };

  const actions = shareActions({
    confirm,
    editTitle,
    renamed,
    gone,
    fail,
  });
  const terminals = shares === null ? null : shares.filter((s) => sectionOf(s.source) === "terminals");
  const agents = shares === null ? null : shares.filter((s) => sectionOf(s.source) === "agents");
  const head: Record<Tab, { title: string; count: number | null; live: number; toReview: number }> = {
    terminals: { title: t("shares:terminalsTitle"), count: terminals?.length ?? null, live: terminals?.filter((s) => s.live).length ?? 0, toReview: 0 },
    agents: { title: t("shares:agentsTitle"), count: agents?.length ?? null, live: agents?.filter((s) => s.live).length ?? 0, toReview: 0 },
    recordings: { title: t("recordings:recordingsTitle"), count: recordings?.n ?? null, live: 0, toReview: recordings?.toReview ?? 0 },
    "class-recordings": { title: t("recordings:classRecordingsTab"), count: classRecordings?.n ?? null, live: 0, toReview: classRecordings?.toReview ?? 0 },
  };
  // Class recordings only for someone with any, or whose kept tab it is
  const shown = tabs.filter((id) => id !== "class-recordings" || tab === id || (classRecordings?.n ?? 0) > 0);

  return (
    <Page className="shares-page">
      {confirmDialog}
      {editDialog}
      <PageHead
        title={t("shares:sharesTitle")}
        actions={
          <button aria-haspopup="dialog" aria-expanded={devicesOpen} aria-controls="devices-drawer" onClick={() => setDevicesOpen(true)}>
            {t("shares:devicesTitle")}
            {devices !== null && <span className="tab-count"> {devices.length}</span>}
          </button>
        }
      />
      {/* the month across every agent share: on whichever tab is open */}
      {agentCap && (
        <p className="agent-cap muted small">
          <span title={t("shares:agentCapWhy")}>{phrases.agentCapLine(agentCap.spent_usd, agentCap.cap_usd, agentCap.set)}</span>{" "}
          <button className="small" onClick={() => void changeCap(agentCap)}>{t("shares:agentCapEdit")}</button>
        </p>
      )}
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={load}>{t("common:retry")}</button>
        </p>
      )}

      <Tabs
        prefix="shares"
        label={t("shares:sharesTabs")}
        value={tab}
        onChange={setTab}
        tabs={shown.map((id) => ({
          id,
          label: (
            <>
              {head[id].title}
              {head[id].count !== null && <span className="tab-count">{head[id].count}</span>}
              {head[id].live > 0 && <span className="tab-live">{t("classes:liveCount", { n: head[id].live })}</span>}
              {head[id].toReview > 0 && <span className="tab-review">{t("shares:toReviewCount", { n: head[id].toReview })}</span>}
            </>
          ),
        }))}
      />

      <div role="tabpanel" id={panelId("shares", "terminals")} aria-labelledby={tabId("shares", "terminals")} hidden={tab !== "terminals"} data-testid="panel-terminals">
        <ShareSection id="terminals" title={t("shares:terminalsTitle")} shares={terminals} empty={t("shares:noTerminals")} actions={actions} />
      </div>
      <div role="tabpanel" id={panelId("shares", "agents")} aria-labelledby={tabId("shares", "agents")} hidden={tab !== "agents"} data-testid="panel-agents">
        <ShareSection
          id="agents"
          title={t("shares:agentsTitle")}
          shares={agents}
          chips
          empty={t("shares:noAgents")}
          actions={actions}
        />
      </div>
      <div role="tabpanel" id={panelId("shares", "recordings")} aria-labelledby={tabId("shares", "recordings")} hidden={tab !== "recordings"} data-testid="panel-recordings">
        <RecordingList load={loadRecordings} onCount={countRecordings} />
      </div>
      <div
        role="tabpanel"
        id={panelId("shares", "class-recordings")}
        aria-labelledby={tabId("shares", "class-recordings")}
        hidden={tab !== "class-recordings"}
        data-testid="panel-class-recordings"
      >
        <RecordingList scope="class" load={loadRecordings} onCount={countClassRecordings} />
      </div>

      <Drawer id="devices-drawer" title={t("shares:devicesTitle")} open={devicesOpen} onClose={() => setDevicesOpen(false)}>
        <DeviceList devices={devices} onRevoke={(d) => void revokeDevice(d)} />
      </Drawer>
    </Page>
  );

  async function revokeDevice(d: Device) {
    if (!(await confirm(t("shares:revokeConfirm", { name: d.name }), { action: t("shares:revoke"), danger: true }))) return;
    try {
      await api.revokeDevice(d.id);
      setDevices((ds) => (ds ?? []).filter((x) => x.id !== d.id));
    } catch (err) {
      fail(err);
    }
  }
}
